/**
 * Workbench draft state: the shape the UI edits, how it is persisted to
 * localStorage (docs/UX.md 7 「草稿自动保存」) and how it becomes a
 * `judgmentSubmitSchema` payload.
 *
 * 红线 14.1 lives here as well as in the render tree: the singing branch never
 * emits `tone` / `realized_tone`, the reading branch never emits
 * `onset` / `nucleus` / `coda` / `zhe_realized`.
 * 红线 14.4: an untouched dimension stays `null`, never 0.
 */
import { MARK_SEVERITY } from "@xinghai/shared";
import type { Domain, JudgmentSubmitPayload, MarkType, Zhe } from "@xinghai/shared";
import type { CharDimKey } from "./labels";
import { CHAR_DIMS, UTTERANCE_ROWS, starsToScore } from "./labels";

/** Default quality for a dimension nobody has touched yet: 正确 = 1.0. */
export const DEFAULT_SCORE = 1.0;

export interface CharDraft {
  initial: number | null;
  final: number | null;
  tone: number | null;
  onset: number | null;
  nucleus: number | null;
  coda: number | null;
  mark_type: MarkType | null;
  /** Canonical→realized phone code (`ZH_I_Z`). */
  realized: string | null;
  /** Free IPA text; emitted in the char `note` as an `IPA: [ts]` prefix. */
  realized_ipa: string | null;
  /** 0–4, 0 = 轻声. Reading only. */
  realized_tone: number | null;
  /** Singing only (§4.3 rhyme_return_error). */
  zhe_realized: Zhe | null;
  note: string | null;
  /** The expert changed something on this card — drives the N/M progress. */
  touched: boolean;
}

export interface WorkDraft {
  recording_id: string;
  domain: Domain;
  chars: CharDraft[];
  /** Utterance rubric as 1–5 stars; normalised to [0,1] on submit. */
  stars: Record<string, number | null>;
  listening_effort: number | null;
  accentedness: number | null;
  note: string;
  started_at: number;
  saved_at: number;
}

export function emptyChar(domain: Domain): CharDraft {
  const reading = domain === "reading";
  return {
    initial: reading ? DEFAULT_SCORE : null,
    final: reading ? DEFAULT_SCORE : null,
    tone: reading ? DEFAULT_SCORE : null,
    onset: reading ? null : DEFAULT_SCORE,
    nucleus: reading ? null : DEFAULT_SCORE,
    coda: reading ? null : DEFAULT_SCORE,
    mark_type: null,
    realized: null,
    realized_ipa: null,
    realized_tone: null,
    zhe_realized: null,
    note: null,
    touched: false,
  };
}

export function emptyDraft(recordingId: string, domain: Domain, charCount: number): WorkDraft {
  const stars: Record<string, number | null> = {};
  for (const [key] of UTTERANCE_ROWS[domain]) stars[key] = null;
  return {
    recording_id: recordingId,
    domain,
    chars: Array.from({ length: charCount }, () => emptyChar(domain)),
    stars,
    listening_effort: null,
    accentedness: null,
    note: "",
    started_at: Date.now(),
    saved_at: Date.now(),
  };
}

// --------------------------------------------------------------------------- //
// auto-consistency (§4.1 severity table) — defaults, always overridable
// --------------------------------------------------------------------------- //

/** Which of the three dimensions a 省略 removed. */
export type OmissionTarget = CharDimKey;

/**
 * Apply the mark type's default scores. `正确` → 1.0 everywhere,
 * `无法辨识` → 0.1 everywhere (severity table), `省略` waits for the
 * 「省略的是」 prompt and only then zeroes that one dimension.
 */
export function applyMarkDefaults(char: CharDraft, domain: Domain, mark: MarkType): CharDraft {
  const dims = CHAR_DIMS[domain];
  const next: CharDraft = { ...char, mark_type: mark, touched: true };
  const slots = next as unknown as Record<CharDimKey, number | null>;
  if (mark === "correct") {
    for (const d of dims) slots[d] = DEFAULT_SCORE;
    next.realized = null;
    next.realized_ipa = null;
    next.realized_tone = null;
    return next;
  }
  // §4.1 severity table pre-fills the sliders; the expert can still drag them.
  // `deletion` waits for the 「省略的是」 prompt, `insertion` has no severity.
  const severity = MARK_SEVERITY[mark];
  if (severity !== null && mark !== "deletion") {
    for (const d of dims) slots[d] = severity;
  }
  return next;
}

/** 省略 → the chosen dimension goes to 0 (confirmed absent, not unlabeled). */
export function applyOmission(char: CharDraft, dim: CharDimKey): CharDraft {
  const next: CharDraft = { ...char, touched: true };
  (next as unknown as Record<CharDimKey, number | null>)[dim] = 0;
  return next;
}

// --------------------------------------------------------------------------- //
// payload
// --------------------------------------------------------------------------- //

/**
 * `realized` must stay a pure phone code (`ZH_I_*` / `ZH_F_*`) or null —
 * `buildManifestRow` writes it straight into `realized_phones`.
 */
export function composeRealized(code: string | null): string | null {
  return code?.trim() || null;
}

/** Free IPA rides along in the char note as an `IPA: [ts]` prefix. */
export function composeCharNote(ipa: string | null, note: string | null): string | null {
  const i = ipa?.trim() || null;
  const n = note?.trim() || null;
  const prefix = i ? `IPA: [${i.replace(/^\[|\]$/g, "")}]` : null;
  if (prefix && n) return `${prefix}；${n}`;
  return prefix ?? n;
}

/** Split `composeCharNote` back apart when restoring a submitted judgment. */
export function decomposeCharNote(value: string | null | undefined): {
  ipa: string | null;
  note: string | null;
} {
  if (!value) return { ipa: null, note: null };
  const m = /^IPA:\s*\[([^\]]*)\]\s*(?:；\s*(.*))?$/s.exec(value);
  if (m) return { ipa: m[1] ?? null, note: m[2]?.trim() || null };
  return { ipa: null, note: value };
}

export interface BuildOptions {
  /**
   * docs/ROADMAP.md A3 — 单字任务 (T2) does not get a 句级 rubric: every
   * utterance dimension goes out as `null` (tri-state allows it).
   */
  skipUtterance?: boolean;
}

export function buildPayload(
  draft: WorkDraft,
  elapsedMs: number,
  opts: BuildOptions = {},
): JudgmentSubmitPayload {
  const singing = draft.domain === "singing";
  const star = (key: string) =>
    opts.skipUtterance ? null : starsToScore(draft.stars[key] ?? null);

  const chars = draft.chars.map((c, char_index) => {
    const realized = composeRealized(c.realized);
    const note = composeCharNote(c.realized_ipa, c.note);
    if (singing) {
      // 红线 14.1 — no tone, no realized_tone; 字头/字腹/字尾 are required keys.
      return {
        char_index,
        initial: null,
        final: null,
        tone: null,
        mark_type: c.mark_type,
        realized,
        realized_tone: null,
        onset: c.onset,
        nucleus: c.nucleus,
        coda: c.coda,
        zhe_realized: c.zhe_realized,
        note,
      };
    }
    // reading — onset/nucleus/coda/zhe_realized must be absent entirely.
    return {
      char_index,
      initial: c.initial,
      final: c.final,
      tone: c.tone,
      mark_type: c.mark_type,
      realized,
      realized_tone: c.realized_tone,
      note,
    };
  });

  const utterance = singing
    ? {
        domain: "singing" as const,
        onset: star("onset"),
        nucleus: star("nucleus"),
        coda: star("coda"),
        intelligibility: star("intelligibility"),
        rhythm: star("rhythm"),
      }
    : {
        domain: "reading" as const,
        accuracy: star("accuracy"),
        completeness: star("completeness"),
        fluency: star("fluency"),
        prosodic: star("prosodic"),
        total: star("total"),
      };

  return {
    domain: draft.domain,
    chars,
    utterance,
    listening_effort: draft.listening_effort,
    accentedness: draft.accentedness,
    note: draft.note.trim() ? draft.note.trim() : null,
    elapsed_ms: Math.max(0, Math.round(elapsedMs)),
  } as JudgmentSubmitPayload;
}

// --------------------------------------------------------------------------- //
// localStorage — keyed by recording + user so a shared machine never leaks
// --------------------------------------------------------------------------- //

export function draftKey(recordingId: string, userId: string): string {
  return `xz.annotator.draft.${userId}.${recordingId}`;
}

export function loadDraft(recordingId: string, userId: string): WorkDraft | null {
  try {
    const raw = window.localStorage.getItem(draftKey(recordingId, userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WorkDraft;
    if (!parsed || parsed.recording_id !== recordingId || !Array.isArray(parsed.chars)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveDraft(userId: string, draft: WorkDraft): void {
  try {
    window.localStorage.setItem(
      draftKey(draft.recording_id, userId),
      JSON.stringify({ ...draft, saved_at: Date.now() }),
    );
  } catch {
    /* private mode / quota — the draft is a convenience, not the record */
  }
}

export function clearDraft(recordingId: string, userId: string): void {
  try {
    window.localStorage.removeItem(draftKey(recordingId, userId));
  } catch {
    /* ignore */
  }
}

// --------------------------------------------------------------------------- //
// 快速模式 (docs/ROADMAP.md B3) — T2 单字 only, off by default
// --------------------------------------------------------------------------- //

const QUICK_KEY = "xz.annotator.quickmode";

export function readQuickMode(): boolean {
  try {
    return window.localStorage.getItem(QUICK_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeQuickMode(on: boolean): void {
  try {
    window.localStorage.setItem(QUICK_KEY, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}

// --------------------------------------------------------------------------- //
// local submission tally — powers 「近 7 天」 when the server sends no series
// --------------------------------------------------------------------------- //

const TALLY_KEY = (userId: string) => `xz.annotator.tally.${userId}`;

export function recordSubmission(userId: string, when = new Date()): void {
  try {
    const key = TALLY_KEY(userId);
    const raw = window.localStorage.getItem(key);
    const map = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    const day = when.toISOString().slice(0, 10);
    map[day] = (map[day] ?? 0) + 1;
    window.localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function readTally(userId: string): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(TALLY_KEY(userId));
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

// --------------------------------------------------------------------------- //
// pace — 平均每条用时 / 预计剩余时间 (docs/UX.md §2.1, §2.2 progress line)
// --------------------------------------------------------------------------- //

const PACE_KEY = (userId: string) => `xz.annotator.pace.${userId}`;

export interface Pace {
  count: number;
  totalMs: number;
  /** Mean seconds per item, or `null` before the first submission. */
  avgMs: number | null;
}

/** Keep the mean over the last ~200 items so it tracks the current session. */
export function recordTiming(userId: string, elapsedMs: number): void {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return;
  try {
    const prev = readPace(userId);
    let count = prev.count + 1;
    let totalMs = prev.totalMs + Math.min(elapsedMs, 15 * 60_000);
    if (count > 200) {
      totalMs = Math.round(totalMs * (200 / count));
      count = 200;
    }
    window.localStorage.setItem(PACE_KEY(userId), JSON.stringify({ count, totalMs }));
  } catch {
    /* ignore */
  }
}

export function readPace(userId: string): Pace {
  try {
    const raw = window.localStorage.getItem(PACE_KEY(userId));
    const p = raw ? (JSON.parse(raw) as { count?: number; totalMs?: number }) : {};
    const count = p.count ?? 0;
    const totalMs = p.totalMs ?? 0;
    return { count, totalMs, avgMs: count > 0 ? totalMs / count : null };
  } catch {
    return { count: 0, totalMs: 0, avgMs: null };
  }
}

// --------------------------------------------------------------------------- //
// 撤回 (docs/UX.md §2.2) — 5 s window, handed to the next screen
// --------------------------------------------------------------------------- //

const RETRACT_KEY = "xz.annotator.retract";
export const RETRACT_WINDOW_MS = 5000;

export interface RetractTicket {
  recording_id: string;
  at: number;
}

export function armRetract(recordingId: string): void {
  try {
    window.sessionStorage.setItem(
      RETRACT_KEY,
      JSON.stringify({ recording_id: recordingId, at: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

/** Returns the ticket only while it is still inside the 5 s window. */
export function readRetract(now = Date.now()): RetractTicket | null {
  try {
    const raw = window.sessionStorage.getItem(RETRACT_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as RetractTicket;
    if (!t?.recording_id || now - t.at > RETRACT_WINDOW_MS) return null;
    return t;
  } catch {
    return null;
  }
}

export function clearRetract(): void {
  try {
    window.sessionStorage.removeItem(RETRACT_KEY);
  } catch {
    /* ignore */
  }
}
